import {
  type AggregationTimeSpan,
  type ChartAggregateFunctionType,
  type ChartVariantType,
} from '@blaze-shared/elements-domain';
import {
  type AggregateWorkflowNode,
  appendNodeToWorkflowMutably,
  CHARTS_COMPUTE_NODES_PREFIX,
  findWorkflowNode,
  isAggregateNode,
  isAggregateNodeWithChartPrefix,
  isComputeNode,
  isNodeIdWithChartsComputePrefix,
  removeWorkflowNodeMutably,
  type Workflow,
  type WorkflowNode,
} from '@blaze-shared/workflow-domain';

interface ChartVariantParams extends Record<ChartVariantType, any> {
  chartSingle: {
    aggregation: ChartAggregateFunctionType | undefined;
    mainValue: string | undefined;
    /* BL- 2584
    compareValue: string | undefined;
    compareAggregation: ChartAggregateFunctionType | undefined;
    */
    alias: string | undefined;
  };
  chartLine: {
    aggregation: ChartAggregateFunctionType | undefined;
    mainValue: string | undefined;
    groupBy: string | undefined;
    segmentData: string | undefined;
    groupByDetails: string | undefined;
    segmentDataDetails: string | undefined;
    alias: string | undefined;
    additionalAggregationsArray: {
      aggregation: ChartAggregateFunctionType | undefined;
      mainValue: string | undefined;
      alias: string | undefined;
    };
  };
  chartPie: {
    aggregation: ChartAggregateFunctionType | undefined;
    mainValue: string | undefined;
    segmentData: string | undefined;
    segmentDataDetails: string | undefined;
    alias: string | undefined;
    additionalAggregationsArray: {
      aggregation: ChartAggregateFunctionType | undefined;
      mainValue: string | undefined;
      alias: string | undefined;
    };
  };
}

export function prepareNodesForSingleValueChartsMutably(
  workflow: Workflow,
  data: { chartType: 'chartSingle' } & ChartVariantParams['chartSingle'],
  columnsAlreadyInDataset: string[],
): void {
  toggleAggregationNodeMutably(workflow, data, columnsAlreadyInDataset);
}

/**
 * Compute the value with the formula and fix the empty values
 * @param value $$input1 with the column name
 * @param formula the formula to compute the value
 * @returns the formula to compute the value with the fix for empty values
 */
export function getComputeByFormula(value: string, formula: string): string {
  const formulaExtra = formula === 'WEEKSTARTDATE' ? ',"Monday"' : '';
  return `${formula}(${value}${formulaExtra})`;
}

export function prepareNodesForLineChartsMutably<T extends ChartVariantType>(
  workflow: Workflow,
  data: {
    chartType: T;
    mainValue: string;
    groupBy: string;
    groupByDetails: AggregationTimeSpan;
    aggregation: string;
    segmentData: string;
    segmentDataDetails: AggregationTimeSpan;
    alias?: string;
    additionalAggregationsArray: {
      mainValue: string;
      alias: string;
      aggregation: string;
    }[];
    columnsAlreadyInDataset: string[];
  } & ChartVariantParams[T],
): void {
  const computeNodes = workflow.nodes.filter(
    (node) => isComputeNode(node) && isNodeIdWithChartsComputePrefix(node),
  );

  for (const node of computeNodes) {
    removeWorkflowNodeMutably(workflow, node);

    const columnAlreadyInDatasetIndex = data.columnsAlreadyInDataset.indexOf(node.name);
    if (columnAlreadyInDatasetIndex !== -1) {
      data.columnsAlreadyInDataset.splice(columnAlreadyInDatasetIndex, 1);
    }
  }

  // we need to remove the aggregation node because the order will be changed, we need to re-place it later with an toggleAggregationNodeMutably operation
  removeWorkflowNodeMutably(
    workflow,
    workflow.nodes.find(
      (node) =>
        isAggregateNode(node) &&
        (isNodeIdWithChartsComputePrefix(node) || isAggregateNodeWithChartPrefix(node)),
    ),
  );

  const dateSplitDetails: AggregationTimeSpan = (
    data.groupByDetails ||
    data.segmentDataDetails ||
    ''
  ).toLowerCase();
  if (dateSplitDetails) {
    const yearNode: WorkflowNode = {
      id: `${CHARTS_COMPUTE_NODES_PREFIX}-year`,
      name: 'computeByYear',
      nodeType: 'Compute',
      nextNodeId: '',
      value: '',
      formula: getComputeByFormula(
        `$$input1["${data.groupByDetails ? data.groupBy : data.segmentData}"]`,
        'YEAR',
      ),
    };
    appendNodeToWorkflowMutably(workflow, yearNode);
    if (dateSplitDetails.indexOf('yearly') === -1) {
      const formulaMap: Record<AggregationTimeSpan, string> = {
        daily: 'DAY',
        weekly: 'WEEKSTARTDATE',
        monthly: 'MONTH',
        quarterly: 'QUARTER',
        yearly: 'YEAR',
      };
      const formula = formulaMap[dateSplitDetails];
      if (formula === 'DAY') {
        const monthNode: WorkflowNode = {
          id: `${CHARTS_COMPUTE_NODES_PREFIX}-month`,
          name: 'computeByMonth',
          nodeType: 'Compute',
          nextNodeId: '',
          value: '',
          formula: getComputeByFormula(
            `$$input1["${data.groupByDetails ? data.groupBy : data.segmentData}"]`,
            'MONTH',
          ),
        };
        appendNodeToWorkflowMutably(workflow, monthNode);
      }
      const yearNode: WorkflowNode = {
        id: `${CHARTS_COMPUTE_NODES_PREFIX}-details`,
        name: 'computeByDetails',
        nodeType: 'Compute',
        nextNodeId: '',
        value: '',
        formula: getComputeByFormula(
          `$$input1["${data.groupByDetails ? data.groupBy : data.segmentData}"]`,
          formula,
        ),
      };
      appendNodeToWorkflowMutably(workflow, yearNode);
    }
  } else if (data.aggregation !== 'COUNT') {
    appendNodeToWorkflowMutably(workflow, {
      id: CHARTS_COMPUTE_NODES_PREFIX,
      name: 'computeBy',
      nodeType: 'Compute',
      nextNodeId: '',
      value: '',
      formula: '1',
    });
  }

  toggleAggregationNodeMutably(workflow, data, data.columnsAlreadyInDataset);
}

export function prepareNodesForPieChartsMutably<T extends ChartVariantType>(
  workflow: Workflow,
  data: {
    chartType: T;
    mainValue: string;
    aggregation: string;
    segmentData: string;
    segmentDataDetails: AggregationTimeSpan;
    columnsAlreadyInDataset: string[];
  } & ChartVariantParams[T],
): void {
  const computeNodes = workflow.nodes.filter(
    (node) => isComputeNode(node) && isNodeIdWithChartsComputePrefix(node),
  );

  for (const node of computeNodes) {
    removeWorkflowNodeMutably(workflow, node);
  }

  // we need to remove the aggregation node because the order will be changed, we need to re-place it later with an toggleAggregationNodeMutably operation
  removeWorkflowNodeMutably(
    workflow,
    workflow.nodes.find(
      (node) =>
        isAggregateNode(node) &&
        (isNodeIdWithChartsComputePrefix(node) || isAggregateNodeWithChartPrefix(node)),
    ),
  );

  const dateSplitDetails: AggregationTimeSpan = (data.segmentDataDetails || '').toLowerCase();
  if (dateSplitDetails) {
    const yearNode: WorkflowNode = {
      id: `${CHARTS_COMPUTE_NODES_PREFIX}-year`,
      name: 'computeByYear',
      nodeType: 'Compute',
      nextNodeId: '',
      value: '',
      formula: getComputeByFormula(`$$input1["${data.segmentData}"]`, 'YEAR'),
    };
    appendNodeToWorkflowMutably(workflow, yearNode);
    if (dateSplitDetails.indexOf('yearly') === -1) {
      const formulaMap: Record<AggregationTimeSpan, string> = {
        daily: 'DAY',
        weekly: 'WEEKSTARTDATE',
        monthly: 'MONTH',
        quarterly: 'QUARTER',
        yearly: 'YEAR',
      };
      const formula = formulaMap[dateSplitDetails];
      if (formula === 'DAY') {
        const monthNode: WorkflowNode = {
          id: `${CHARTS_COMPUTE_NODES_PREFIX}-month`,
          name: 'computeByMonth',
          nodeType: 'Compute',
          nextNodeId: '',
          value: '',
          formula: getComputeByFormula(`$$input1["${data.segmentData}"]`, 'MONTH'),
        };
        appendNodeToWorkflowMutably(workflow, monthNode);
      }
      const yearNode: WorkflowNode = {
        id: `${CHARTS_COMPUTE_NODES_PREFIX}-details`,
        name: 'computeByDetails',
        nodeType: 'Compute',
        nextNodeId: '',
        value: '',
        formula: getComputeByFormula(`$$input1["${data.segmentData}"]`, formula),
      };
      appendNodeToWorkflowMutably(workflow, yearNode);
    }
  } else if (data.aggregation !== 'COUNT') {
    appendNodeToWorkflowMutably(workflow, {
      id: CHARTS_COMPUTE_NODES_PREFIX,
      name: 'computeBy',
      nodeType: 'Compute',
      nextNodeId: '',
      value: '',
      formula: '1',
    });
  }

  toggleAggregationNodeMutably(workflow, data, data.columnsAlreadyInDataset);
}

/**
 * Prepare the groupByColumns for the toggleAggregationNode
 * @param groupByColumns columns that user wants to group by or prop editor points to
 * @param detailGrouping when the detailGrouping is set, we need to append the computed columns to the groupByColumns eg. computeByYear, computeByMonth
 * @param columnsAlreadyInDataset when the columns are already in the dataset, we need to append 'Computed_' to the column name (corner case)
 * @returns
 */
export function prepareGroupByColumnsForToggleAggregationNode(
  groupByColumns: string[],
  detailGrouping: string,
  columnsAlreadyInDataset: string[],
  groupByDetails?: string,
  segmentDataDetails?: string,
): string[] {
  if (detailGrouping) {
    // NOTE this is to replace aggregation by a date field,
    // with more specific date-aggregation columns (appended with compute nodes)
    const groupByCols = [];
    const daily = 'daily';
    const yearly = 'yearly';

    const computeByYearComputedColumnName =
      (columnsAlreadyInDataset.includes('computeByYear') ? 'Computed_' : '') + 'computeByYear';
    const computeByMonthComputedColumnName =
      (columnsAlreadyInDataset.includes('computeByMonth') ? 'Computed_' : '') + 'computeByMonth';
    const computeByDetailsComputedColumnName =
      (columnsAlreadyInDataset.includes('computeByDetails') ? 'Computed_' : '') +
      'computeByDetails';
    groupByCols.push(computeByYearComputedColumnName);
    if (detailGrouping === daily) {
      groupByCols.push(computeByMonthComputedColumnName, computeByDetailsComputedColumnName);
    } else if (detailGrouping !== yearly) {
      groupByCols.push(computeByDetailsComputedColumnName);
    }
    if (groupByDetails) {
      groupByColumns.shift();
      groupByColumns.unshift(...groupByCols);
    } else if (segmentDataDetails) {
      groupByColumns.pop();
      groupByColumns.push(...groupByCols);
    }
  }
  return groupByColumns;
}

export function toggleAggregationNodeMutably<T extends ChartVariantType>(
  workflow: Workflow,
  data: { chartType: T } & ChartVariantParams[T],
  columnsAlreadyInDataset: string[],
): AggregateWorkflowNode {
  let node = findWorkflowNode(
    workflow.nodes,
    isAggregateNode,
    'aggregate',
    'chart-',
  ) as AggregateWorkflowNode;
  const alias = data.alias ?? 'chartAggregation';
  switch (data.chartType) {
    case 'chartSingle':
      if (!data.aggregation || data.aggregation === 'FIELD VALUE') {
        removeWorkflowNodeMutably(workflow, node);
      } else {
        const functionDefs = [];

        if (data.mainValue || data.aggregation === 'COUNT') {
          functionDefs.push({ name: data.aggregation, column: data.mainValue || '', alias });

          // BL-2584
          // if ((data.compareValue || data.compareAggregation === 'COUNT') && data.compareAggregation && data.compareAggregation !== 'FIELD VALUE') {
          //   functionDefs.push({ name: data.compareAggregation, column: data.compareValue || '' });
          // }
        }

        if (!node) {
          node = {
            id: 'chart-aggregate',
            nodeType: 'Aggregate',
            nextNodeId: '',
            functionDefs,
            groupByColumns: [],
            functions: [],
          };
          appendNodeToWorkflowMutably(workflow, node);
        } else {
          node.functionDefs = functionDefs;
        }
      }
      break;
    case 'chartLine':
      if (!data.aggregation || data.aggregation === 'FIELD VALUE') {
        removeWorkflowNodeMutably(workflow, node);
      } else {
        const functionDefs = [{ name: data.aggregation, column: data.mainValue || '', alias }];
        const groupByColumns = prepareGroupByColumnsForToggleAggregationNode(
          [data.groupBy, data.segmentData],
          data.groupByDetails || data.segmentDataDetails,
          columnsAlreadyInDataset,
          data.groupByDetails,
          data.segmentDataDetails,
        );

        interface AdditionalAggregationData {
          alias: string;
          aggregation: string;
          mainValue: string;
        }

        (data.additionalAggregationsArray || []).forEach(
          (additionalAggregationData: AdditionalAggregationData) => {
            functionDefs.push({
              name: additionalAggregationData.aggregation,
              column: additionalAggregationData.mainValue || '',
              alias: additionalAggregationData.alias,
            });
          },
        );

        if (!node) {
          node = {
            id: 'chart-aggregate',
            nodeType: 'Aggregate',
            nextNodeId: '',
            functions: [],
            functionDefs,
            groupByColumns,
          };
          appendNodeToWorkflowMutably(workflow, node);
        } else {
          node.functionDefs = functionDefs;
          node.groupByColumns = groupByColumns;
        }
      }
      break;
    case 'chartDoughnut':
    case 'chartPie':
      if (!data.aggregation || data.aggregation === 'FIELD VALUE') {
        removeWorkflowNodeMutably(workflow, node);
      } else {
        const functionDefs = [{ name: data.aggregation, column: data.mainValue || '', alias }];
        const groupByColumns = prepareGroupByColumnsForToggleAggregationNode(
          [data.segmentData],
          data.segmentDataDetails,
          columnsAlreadyInDataset,
          data.groupByDetails,
          data.segmentDataDetails,
        );
        if (!node) {
          node = {
            id: 'chart-aggregate',
            nodeType: 'Aggregate',
            nextNodeId: '',
            functions: [],
            functionDefs,
            groupByColumns,
          };
          appendNodeToWorkflowMutably(workflow, node);
        } else {
          node.functionDefs = functionDefs;
          node.groupByColumns = groupByColumns;
        }
      }
      break;
    default:
      break;
  }
  return node;
}
